import type {
  TaktBoardResponse,
  TaktEntry,
  TaktGroup,
  TaktSection,
} from "../../../models";

/**
 * The generic "board attribute" model that powers both Filter and Display.
 *
 * Every dimension a user can reason about on the board — Trade, Critical path,
 * Takt work code, and any future one ("modified on this schedule", status, …) —
 * is described once as a {@link TaktAttribute}. From that single descriptor the
 * board can: (a) FILTER to the activities matching selected values (everything
 * else is removed — rows, zones, and sections that have nothing left vanish),
 * and (b) COLOR bars by the dimension. Adding a new dimension is one entry in
 * {@link buildAttributes} — no new UI surface.
 */

/** Which dimension the bars are colored by. */
export type ColorByMode = "none" | "workCode" | "trade" | "critical";

/** How active filters across different attributes combine. */
export type MatchMode = "all" | "any";

/** Numeric compare operator for range attributes. */
export type CompareOp = "eq" | "gt" | "lt";

export interface AttrValue {
  id: string;
  label: string;
  /** Short code shown alongside the label (e.g. the work code "A1"), when the
   *  label is a descriptive name. Searchable. */
  code?: string;
  /** Optional swatch colors for the picker / chips. */
  fill?: string;
  font?: string;
}

export interface TaktAttribute {
  key: string;
  label: string;
  kind: "enum" | "boolean" | "range";
  /** Value ids an entry carries for this attribute (empty for ranges). */
  entryValues: (entry: TaktEntry) => string[];
  /** Full ordered domain of values, for the value picker / legend (empty for ranges). */
  domain: AttrValue[];
  /** Offer a search box in the value picker (for long domains like work codes). */
  searchable?: boolean;
  /** Each value also navigates the board (e.g. Section → scroll to it). Renders a
   *  jump affordance per value alongside the filter toggle. */
  navigable?: boolean;
  /** For range attributes: the entry's numeric value (null = no value). */
  entryNumber?: (entry: TaktEntry) => number | null;
  /** For range attributes: unit suffix (e.g. "days"). */
  unit?: string;
}

/** attribute key → selected value ids. An attribute with a non-empty array is
 *  an active filter. */
export type FilterState = Record<string, string[]>;

/* ------------------------------------------------------------------ */
/*  Building attributes from board data                                */
/* ------------------------------------------------------------------ */

function forEachEntry(
  data: TaktBoardResponse,
  fn: (entry: TaktEntry) => void,
): void {
  for (const section of data.sections) {
    for (const group of section.groups) {
      for (const entry of group.entries) fn(entry);
    }
  }
}

/** Alpha-first, then numeric ascending — matches the section legend ordering. */
function sortCodes(codes: string[]): string[] {
  return codes.sort((a, b) => {
    const aNum = /^\d+$/.test(a);
    const bNum = /^\d+$/.test(b);
    if (aNum && bNum) return parseInt(a, 10) - parseInt(b, 10);
    if (!aNum && !bNum) return a.localeCompare(b);
    return aNum ? 1 : -1;
  });
}

export function buildAttributes(data: TaktBoardResponse): TaktAttribute[] {
  const attrs: TaktAttribute[] = [];

  // ----- Section (structural — which section a bar lives in) -----
  // Section isn't an intrinsic entry attribute, so map each entry to its
  // section by object identity (the same entry refs flow through applyFilters /
  // facetCounts, so the lookup stays valid). Doubles as navigation: selecting a
  // value isolates the board to it, the per-row jump scrolls to it.
  if (data.sections.length > 0) {
    const sectionOf = new Map<TaktEntry, string>();
    for (const section of data.sections) {
      for (const group of section.groups) {
        for (const entry of group.entries) sectionOf.set(entry, section.name);
      }
    }
    attrs.push({
      key: "section",
      label: "Section",
      kind: "enum",
      searchable: true,
      navigable: true,
      domain: data.sections.map((s) => ({ id: s.name, label: s.name })),
      entryValues: (e) => {
        const name = sectionOf.get(e);
        return name ? [name] : [];
      },
    });
  }

  // ----- Trade (only when trades are configured) -----
  const trades = data.trades || [];
  if (trades.length > 0) {
    const taskCodeToTrades = new Map<string, string[]>();
    for (const t of trades) {
      const id = String(t.id);
      for (const tc of t.task_codes) {
        const arr = taskCodeToTrades.get(tc);
        if (arr) arr.push(id);
        else taskCodeToTrades.set(tc, [id]);
      }
    }
    const domain: AttrValue[] = [...trades]
      .sort((a, b) => a.display_order - b.display_order)
      .map((t) => ({
        id: String(t.id),
        label: t.name,
        fill: t.fill_color,
        font: t.font_color,
      }));
    attrs.push({
      key: "trade",
      label: "Trade",
      kind: "enum",
      domain,
      entryValues: (e) => {
        const ids = new Set<string>();
        for (const tc of e.task_codes) {
          const found = taskCodeToTrades.get(tc);
          if (found) for (const id of found) ids.add(id);
        }
        return ids.size ? Array.from(ids) : [];
      },
    });
  }

  // ----- Critical path (boolean) -----
  attrs.push({
    key: "critical",
    label: "Critical path",
    kind: "boolean",
    domain: [
      { id: "yes", label: "On critical path", fill: "#e63946", font: "#ffffff" },
    ],
    entryValues: (e) => (e.is_critical ? ["yes"] : []),
  });

  // ----- Takt work code (enum, long → searchable) -----
  const wcColors = data.static_colors || data.colors || {};
  // Work codes are shown and searched by their descriptive name (the entry
  // label / Takt prefix), with the code kept visible alongside.
  const wcLabels = new Map<string, string>();
  forEachEntry(data, (e) => {
    if (!wcLabels.has(e.work_code)) wcLabels.set(e.work_code, e.label || e.work_code);
  });
  const wcDomain: AttrValue[] = sortCodes(Array.from(wcLabels.keys())).map((code) => {
    const c = wcColors[code];
    return {
      id: code,
      label: wcLabels.get(code) || code,
      code,
      fill: c?.fill,
      font: c?.font,
    };
  });
  attrs.push({
    key: "workCode",
    label: "Work code",
    kind: "enum",
    searchable: true,
    domain: wcDomain,
    entryValues: (e) => [e.work_code],
  });

  // ----- Total float (numeric compare) -----
  let hasFloat = false;
  forEachEntry(data, (e) => {
    if (e.float_days != null) hasFloat = true;
  });
  if (hasFloat) {
    attrs.push({
      key: "float",
      label: "Total float",
      kind: "range",
      unit: "days",
      domain: [],
      entryValues: () => [],
      entryNumber: (e) => e.float_days,
    });
  }

  return attrs;
}

/* ------------------------------------------------------------------ */
/*  Filtering (activity / row level)                                   */
/* ------------------------------------------------------------------ */

export function activeFilterKeys(filters: FilterState): string[] {
  return Object.keys(filters).filter(
    (k) => filters[k] && filters[k].length > 0,
  );
}

export function countActiveFilters(filters: FilterState): number {
  return activeFilterKeys(filters).length;
}

/** Encode/decode a numeric compare filter into the string-array FilterState, so
 *  it lives in the same shape as set filters (e.g. "gt:5", "eq:0", "lt:10"). */
export function encodeCompare(op: CompareOp, value: number): string {
  return `${op}:${value}`;
}

export function decodeCompare(
  s: string,
): { op: CompareOp; value: number } | null {
  const i = s.indexOf(":");
  if (i < 0) return null;
  const op = s.slice(0, i);
  const value = Number(s.slice(i + 1));
  if ((op !== "eq" && op !== "gt" && op !== "lt") || Number.isNaN(value)) {
    return null;
  }
  return { op, value };
}

type EntryPredicate = (entry: TaktEntry) => boolean;

/** One predicate per active filter — set membership for enum/boolean, numeric
 *  bounds for range. */
function buildPredicates(
  attributes: TaktAttribute[],
  filters: FilterState,
): EntryPredicate[] {
  const byKey = new Map(attributes.map((a) => [a.key, a]));
  const preds: EntryPredicate[] = [];
  for (const k of activeFilterKeys(filters)) {
    const attr = byKey.get(k);
    if (!attr) continue;
    if (attr.kind === "range" && attr.entryNumber) {
      const cmp = decodeCompare(filters[k][0] || "");
      if (!cmp) continue;
      const getN = attr.entryNumber;
      preds.push((e) => {
        const n = getN(e);
        if (n == null) return false;
        if (cmp.op === "eq") return n === cmp.value;
        if (cmp.op === "gt") return n > cmp.value;
        return n < cmp.value;
      });
    } else {
      const sel = new Set(filters[k]);
      preds.push((e) => attr.entryValues(e).some((v) => sel.has(v)));
    }
  }
  return preds;
}

/**
 * Project the board down to the activities that survive the active filters.
 * Non-matching activities are dropped; groups (zones) and sections left empty
 * disappear. `all` = an activity must satisfy every active filter; `any` = at
 * least one. Returns the original `data` reference when nothing is filtering.
 */
export function applyFilters(
  data: TaktBoardResponse,
  attributes: TaktAttribute[],
  filters: FilterState,
  matchMode: MatchMode,
): TaktBoardResponse {
  const preds = buildPredicates(attributes, filters);
  if (preds.length === 0) return data;
  const passes = (e: TaktEntry): boolean =>
    matchMode === "all" ? preds.every((p) => p(e)) : preds.some((p) => p(e));

  const sections: TaktSection[] = [];
  for (const section of data.sections) {
    const groups: TaktGroup[] = [];
    for (const group of section.groups) {
      const entries = group.entries.filter(passes);
      if (entries.length > 0) groups.push({ ...group, entries });
    }
    if (groups.length > 0) sections.push({ ...section, groups });
  }
  return { ...data, sections };
}

/** Total number of activity entries on the board. */
export function countEntries(data: TaktBoardResponse | null): number {
  if (!data) return 0;
  let n = 0;
  forEachEntry(data, () => {
    n += 1;
  });
  return n;
}

/**
 * For one attribute, how many activities each value would match — counted
 * against the set already narrowed by the OTHER active filters (proper faceted
 * counts). Drives the per-value "(N)" counts and greying-out of zero options.
 */
export function facetCounts(
  data: TaktBoardResponse | null,
  attributes: TaktAttribute[],
  filters: FilterState,
  matchMode: MatchMode,
  attrKey: string,
): Record<string, number> {
  const result: Record<string, number> = {};
  if (!data) return result;
  const attr = attributes.find((a) => a.key === attrKey);
  if (!attr) return result;
  for (const value of attr.domain) result[value.id] = 0;

  const others: FilterState = {};
  for (const k of activeFilterKeys(filters)) {
    if (k !== attrKey) others[k] = filters[k];
  }
  const base = applyFilters(data, attributes, others, matchMode);
  forEachEntry(base, (e) => {
    for (const v of attr.entryValues(e)) {
      if (v in result) result[v] += 1;
    }
  });
  return result;
}

/* ------------------------------------------------------------------ */
/*  Display helpers                                                    */
/* ------------------------------------------------------------------ */

/** Human summary of an attribute's current selection, e.g.
 *  "Electrical, Plumbing" or "A1, A2, B3 +4". */
export function selectionSummary(
  attr: TaktAttribute,
  ids: string[],
  max = 3,
): string {
  if (attr.kind === "range") {
    const cmp = decodeCompare(ids[0] || "");
    if (!cmp) return "any";
    const unit = attr.unit ? ` ${attr.unit}` : "";
    const sym = cmp.op === "eq" ? "=" : cmp.op === "gt" ? ">" : "<";
    return `${sym} ${cmp.value}${unit}`;
  }
  const labelById = new Map(attr.domain.map((v) => [v.id, v.label]));
  const labels = ids.map((id) => labelById.get(id) ?? id);
  if (labels.length <= max) return labels.join(", ");
  return `${labels.slice(0, max).join(", ")} +${labels.length - max}`;
}
